import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { ActionButton, FormDialog, InputField } from "../v3";
import { TaskService } from "../../services";
import { taskProgressValidationRules } from "../../pages/task/validation";
import { FileUploadField } from "./FileUploadField.component";

export const AddEditTaskProgress = ({
  open = false,
  onClose = () => {},
  progressId = undefined,
  taskQuantity = 0,
  taskProgressQuantity = 0,
  taskStatus = "",
  onSuccess = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentQuantity, setCurrentQuantity] = useState(0);
  const [progressDetails, setProgressDetails] = useState({ description: undefined, quantity: undefined });
  const [file, setFile] = useState<File | null>(null);
  const { id } = useParams();

  useEffect(() => {
    if (open && progressId) fetchData(progressId);
    else if (open && !progressId) handleClearForm();
  }, [open, progressId]);

  const fetchData = async (progressId) => {
    setFile(null);
    try {
      const { data: progress } = await TaskService.getTaskProgressById(id, progressId);
      setProgressDetails({
        description: progress?.description,
        quantity: progress?.quantity,
      });
      setCurrentQuantity(progress?.quantity);
    } catch (error) {
      toast.error("Failed to fetch task progress");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setProgressDetails((prev) => ({ ...prev, [name]: value }));
    if (name === "quantity") {
      const num = parseFloat(value);
      const integerRegex = /^[1-9]\d*$/;
      const remainingQuantity = taskQuantity - taskProgressQuantity + currentQuantity;

      if (value !== "" && (isNaN(num) || num <= 0)) {
        setErrors((prev) => ({ ...prev, quantity: "Quantity must be greater than 0" }));
      } else if (value !== "" && !integerRegex.test(value)) {
        setErrors((prev) => ({ ...prev, quantity: "Decimal values are not allowed" }));
      } else if (num > remainingQuantity) {
        setErrors((prev) => ({ ...prev, quantity: `Cannot exceed remaining (${remainingQuantity})` }));
      } else {
        setErrors((prev) => {
          const { quantity, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleClearForm = () => {
    setProgressDetails({ description: undefined, quantity: undefined });
    setFile(null);
    setCurrentQuantity(0);
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const handleSaveProgress = async () => {
    if (loading) return;

    setErrors({});

    if (progressDetails.quantity) progressDetails.quantity = Number(progressDetails.quantity);

    const validationErrors = taskProgressValidationRules(progressDetails);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // validate quantity
    const currentProgressQuantity = taskQuantity - taskProgressQuantity + currentQuantity;
    if (progressDetails.quantity > currentProgressQuantity) {
      setErrors((prev) => ({ ...prev, quantity: `Cannot exceed remaining (${currentProgressQuantity})` }));
      return;
    }

    // calculate quantity other than current task progress
    const excludingCurrentQuantity = taskProgressQuantity - currentQuantity;
    let savedProgressId = progressId;
    try {
      setLoading(true);
      const totalQuantity = taskProgressQuantity + progressDetails.quantity;
      // updating task
      if (progressId) {
        if (taskProgressQuantity !== taskQuantity && excludingCurrentQuantity + progressDetails?.quantity === taskQuantity) {
          try {
            // update task status to DONE while editing an INPROGRESS task
            await TaskService.updateTask(id, { status: "DONE" });
          } catch (error) {
            toast.error("Failed to update task status.");
            return;
          }

          try {
            // update task progress after updating task status to DONE
            await TaskService.updateTaskProgress(id, progressId, progressDetails);
          } catch (error) {
            // failed to update progress, rollback status ( can be either TODO or INPROGRESS )
            try {
              await TaskService.updateTask(id, { status: taskStatus });
            } catch (error) {
              console.error("Rollback failed:", error);
            }
            toast.error("Failed to update task progress.");
            return;
          }
        } else if (taskProgressQuantity === taskQuantity && progressDetails.quantity < currentProgressQuantity) {
          try {
            // update task status to INPROGRESS while editing a completed task
            await TaskService.updateTask(id, { status: "INPROGRESS" });
          } catch (error) {
            toast.error("Failed to update task progress status.");
            return;
          }

          try {
            // update task progress after updating task status to INPROGRESS
            await TaskService.updateTaskProgress(id, progressId, progressDetails);
          } catch (error) {
            // failed to update progress, rollback status
            try {
              await TaskService.updateTask(id, { status: "DONE" });
            } catch (error) {
              console.error("Rollback failed:", error);
            }
            toast.error("Failed to update task progress.");
            return;
          }
        } else {
          await TaskService.updateTaskProgress(id, progressId, progressDetails);
        }
        // creating task
      } else {
        if (taskProgressQuantity === 0 && taskQuantity !== progressDetails.quantity) {
          try {
            // update task status while adding first progress( task quantity and progress quantity are different)
            await TaskService.updateTask(id, { status: "INPROGRESS" });
          } catch (error) {
            toast.error("Failed to create task progress.");
            return;
          }

          try {
            // create task progress after updating task status to INPROGRESS
            const { data: createdProgress } = await TaskService.createTaskProgress(id, progressDetails);
            savedProgressId = createdProgress?.id;
          } catch (error) {
            // failed to create progress, rollback status
            try {
              await TaskService.updateTask(id, { status: "TODO" });
            } catch (error) {
              console.error("Rollback failed:", error);
            }
            toast.error("Failed to create task progress.");
            return;
          }
        } else if (taskProgressQuantity === 0 && taskQuantity === progressDetails.quantity) {
          try {
            // update task status while adding first progress and full quantity
            await TaskService.updateTask(id, { status: "DONE" });
          } catch (error) {
            toast.error("Failed to create task progress.");
            return;
          }

          try {
            // create task progress after updating task status to DONE
            const { data: createdProgress } = await TaskService.createTaskProgress(id, progressDetails);
            savedProgressId = createdProgress?.id;
          } catch (error) {
            // failed to create progress, rollback status
            try {
              await TaskService.updateTask(id, { status: "TODO" });
            } catch (error) {
              console.error("Rollback failed:", error);
            }
            toast.error("Failed to create task progress.");
            return;
          }
        } else if (totalQuantity === taskQuantity) {
          // update task status to DONE when adding final progress
          try {
            await TaskService.updateTask(id, { status: "DONE" });
          } catch (error) {
            toast.error("Failed to create task progress.");
            return;
          }

          // create progress after updating task status to DONE
          try {
            const { data: createdProgress } = await TaskService.createTaskProgress(id, progressDetails);
            savedProgressId = createdProgress?.id;
          } catch (error) {
            try {
              await TaskService.updateTask(id, { status: "DONE" });
            } catch (error) {
              console.error("Rollback failed:", error);
            }
            toast.error("Failed to create task progress.");
            return;
          }
        } else {
          // task already in INPROGRESS, only create task progress
          const { data: createdProgress } = await TaskService.createTaskProgress(id, progressDetails);
          savedProgressId = createdProgress?.id;
        }
      }

      if (file) {
        if (!savedProgressId) {
          toast.warning("Progress saved, but attachment could not be uploaded because the progress ID is missing");
          onSuccess?.();
          handleCloseModal();
          return;
        }

        const formData = new FormData();
        formData.append("attachment", file);

        try {
          await TaskService.uploadTaskProgressAttachment(id, savedProgressId, formData);
        } catch (uploadError) {
          console.error("Failed to upload task progress attachment:", uploadError);

          if (uploadError?.response?.data?.errorCode === "TASK_PROGRESS_ATTACHMENT_LIMIT_REACHED") {
            toast.warning("Maximum 5 attachments are allowed per task progress");
          } else {
            toast.warning("Unable to confirm attachment upload. Please check attachments.");
          }

          onSuccess?.();
          handleCloseModal();
          return;
        }
      }

      toast.success(`Progress ${progressId ? "updated" : "added"} successfully`);
      onSuccess?.();
      handleCloseModal();
    } catch (error) {
      console.error("🚀 ~ handleSaveProgress ~ error:", error);
      toast.error(`Failed to ${progressId ? "edit" : "create"} task progress`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title={progressId ? "Edit Progress" : "Add Progress"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveProgress} disabled={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Description"
            name="description"
            value={progressDetails.description}
            onChange={updateDetailsState}
            error={!!errors.description}
            helperText={errors.description}
            fullWidth
          />
          <InputField
            required
            type="number"
            label=" Quantity"
            name="quantity"
            value={progressDetails.quantity}
            onChange={updateDetailsState}
            error={!!errors.quantity}
            helperText={errors.quantity}
            fullWidth
          />
        </Box>
        <FileUploadField file={file} onChange={setFile} disabled={loading} />
      </Box>
    </FormDialog>
  );
};
